/* eslint-disable react-native/no-inline-styles */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { DocketStatus, NotifierDocketItems } from '@oolio-group/domain';
import BumpItemIndicator from './BumpItemIndicator';
import DocketItemTimer from './DocketItemTimer';
import { useInvokeWithDelay } from '../../../hooks/useInvokeWithDelay';
import { DocketBumper } from '../../../screens/Main/Dockets/DocketBumper';
import theme from '../../../common/theme';
import DocketItemStyles from '../DocketItem.styles';
import { useColors } from '../../../hooks/useColors';
import Icon from '../../Icon/Icon';
import { headerColorSubject } from './DocketItemTimer';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { selectedDocketVar, bulkSelectVar } from '../../../state/apolloVars';
import { useReactiveVar } from '@apollo/client';
import { getDocketHeaderText } from '../../../utils/docketHelper/getDocketHeaderText';

export interface DocketItemHeaderProps {
  docketId: string;
  orderTypeName: string;
  orderTypeShortName: string;
  salesChannelName: string;
  staffName?: string;
  showStaffName?: boolean;
  orderNumber: string;
  docketName: string;
  createdTime: string;
  onPressDocketHeader: () => Promise<void>;
  status: DocketStatus;
  docketHeaderColor?: string;
  docketStatus: DocketStatus;
  isTraining?: boolean;
  notifierItems?: NotifierDocketItems[];
  onSelectDocket?: () => void;
  enablePrint?: boolean;
  printKitchenDocket?: (
    docketId: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => void;
}

const DocketItemHeader: React.FC<DocketItemHeaderProps> = ({
  docketId,
  orderTypeName,
  orderTypeShortName,
  staffName,
  showStaffName = false,
  onPressDocketHeader,
  orderNumber,
  docketName,
  createdTime,
  status,
  docketHeaderColor = theme.colors.primary,
  docketStatus,
  salesChannelName,
  isTraining,
  notifierItems,
  onSelectDocket,
  enablePrint,
  printKitchenDocket,
}) => {
  const { colors } = useColors();
  const context = useContext(DocketBumper);
  // One tap, one attempt. The auto-retry effect below re-invokes a bump for
  // any docket still listed in `context.current` once the bump is no longer
  // in flight, and that queue is only cleared when the request comes back
  // having actually bumped something (useDockets bumpDocketWithStatus). A
  // rejection, or a response that bumps nothing, therefore left the docket
  // queued and the effect firing again — spaced out by the old 500ms pre-bump
  // delay, but a tight request loop without it. De-queue on every outcome so
  // retrying takes a deliberate tap.
  const handleBump = useCallback(async () => {
    // Queue only once the hook has accepted the invocation. Doing it on press
    // instead let a second tap during an in-flight bump de-queue the docket —
    // the tap itself is ignored, but BumpItemIndicator keys off this queue, so
    // the indicator vanished while the request was still running.
    if (!context.current?.includes(docketId)) {
      context.current = [...(context.current || []), docketId];
    }
    try {
      await onPressDocketHeader();
    } finally {
      context.current = context.current?.filter(id => id != docketId) || null;
    }
  }, [context, docketId, onPressDocketHeader]);

  const { loading: bumpLoading, invokeFunction: invokeBump } =
    useInvokeWithDelay({ callBack: handleBump });
  const touchable = useRef(null);

  const [milestoneColor, setMilestoneColor] = useState<string | undefined>(
    () => headerColorSubject.getValue()[docketId],
  );

  const isBulkMode = useReactiveVar(bulkSelectVar);
  const selectedDockets = useReactiveVar(selectedDocketVar);

  const isSelected = Object.keys(selectedDockets).includes(docketId);

  const styles = DocketItemStyles({ isSelected: isBulkMode && isSelected });

  const handlePrintKitchenDocket = useCallback(() => {
    if (enablePrint && printKitchenDocket) {
      printKitchenDocket(docketId, status, notifierItems);
    }
  }, [docketId, enablePrint, notifierItems, printKitchenDocket, status]);

  const docketColor = milestoneColor || docketHeaderColor;

  useEffect(() => {
    const sub: Subscription = headerColorSubject
      .pipe(
        map(colors => colors[docketId]),
        distinctUntilChanged(),
      )
      .subscribe(setMilestoneColor);

    return () => sub.unsubscribe();
  }, [docketId]);

  useEffect(() => {
    if (
      docketStatus == DocketStatus.CREATED &&
      context.current?.includes(docketId) &&
      !bumpLoading
    ) {
      invokeBump();
    }
  }, [bumpLoading, context, docketId, invokeBump, docketStatus, orderNumber]);

  const headerText = getDocketHeaderText({
    orderTypeName,
    orderTypeShortName,
    salesChannelName,
    staffName,
    showStaffName,
  });

  const headerBgColor =
    status == DocketStatus.CREATED ? docketColor : colors.green;

  return (
    <TouchableOpacity
      testID="docket-header"
      onPress={invokeBump}
      disabled={status == DocketStatus.COMPLETED || isBulkMode || isSelected}
      style={[
        enablePrint || status == DocketStatus.CREATED
          ? styles.headerSettings
          : styles.header,
        {
          backgroundColor: headerBgColor,
        },
        isTraining && styles.headerSpacingForTraining,
        isBulkMode && isSelected && styles.headerSelected,
      ]}
    >
      {context?.current?.includes(docketId) && bumpLoading ? (
        <BumpItemIndicator />
      ) : (
        <>
          <View style={styles.headerContent}>
            {isBulkMode && (
              <TouchableOpacity
                testID="btn-select"
                onPress={onSelectDocket}
                style={styles.btnContainer}
              >
                <Icon
                  name={isSelected ? 'check-square' : 'square-full'}
                  color={isSelected ? colors.positive : colors.white}
                />
              </TouchableOpacity>
            )}

            <View style={styles.rowGroup}>
              <View style={styles.row}>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={[styles.text, styles.headerTitle]}
                >
                  {headerText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[styles.text, styles.headerOrderNumber]}
                >
                  {orderNumber}
                </Text>
              </View>
              <View style={styles.row}>
                <Text
                  numberOfLines={1}
                  style={[styles.text, { flex: 1 }]}
                  ellipsizeMode="tail"
                >
                  {docketName}
                </Text>
                <DocketItemTimer
                  createdTime={createdTime}
                  status={status}
                  docketId={docketId}
                />
              </View>
            </View>
            {!isBulkMode && status !== DocketStatus.COMPLETED && (
              <TouchableOpacity
                testID="btn-settings"
                onPress={onSelectDocket}
                ref={touchable}
                style={styles.btnContainer}
              >
                <Icon name={'ellipsis-v'} color={colors.white} />
              </TouchableOpacity>
            )}
            {enablePrint && status === DocketStatus.COMPLETED && (
              <TouchableOpacity
                testID="btn-print"
                onPress={handlePrintKitchenDocket}
                ref={touchable}
                style={styles.btnContainer}
              >
                <Icon name={'print'} color={colors.white} />
              </TouchableOpacity>
            )}
          </View>
        </>
      )}
    </TouchableOpacity>
  );
};

export default DocketItemHeader;
